import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronLeft, Save, Camera, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getProfilePhotoUrl } from '../lib/imageUtils';
import ChangePasswordSection from '../components/ChangePasswordSection';

const ProfilePage = () => {
  const { user, api, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  // P0 CONTACT DETAILS: Extended form fields
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    location: user?.location || '',
    bio: user?.bio || '',
    address_line: user?.address_line || '',
    city: user?.city || '',
    state: user?.state || '',
    postcode: user?.postcode || '',
    country: user?.country || 'Australia'
  });

  const australianStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

  const photoUrl = getProfilePhotoUrl(user?.profile_photo);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, WebP, or HEIC)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    setPhotoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update user context with new photo
      updateUser({ ...user, profile_photo: response.data.s3_key });
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setPhotoLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.put('/users/profile', formData);
      updateUser(response.data);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-semibold">Edit Profile</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Profile Photo Section */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="font-heading">Profile Photo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                {photoUrl ? (
                  <img 
                    src={photoUrl}
                    alt={user?.full_name || 'Profile'}
                    className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling?.classList?.remove('hidden');
                    }}
                    data-testid="profile-photo-preview"
                  />
                ) : null}
                <div className={`w-28 h-28 rounded-full bg-[#004B8D] flex items-center justify-center text-white text-3xl font-semibold shadow-lg ${photoUrl ? 'hidden' : ''}`}>
                  {user?.full_name?.charAt(0)?.toUpperCase() || <User className="w-12 h-12" />}
                </div>
                
                {/* Upload Button Overlay */}
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoLoading}
                  className="absolute bottom-0 right-0 bg-[#004B8D] hover:bg-[#003865] text-white rounded-full p-2.5 shadow-lg transition-colors disabled:opacity-50"
                  data-testid="upload-photo-btn"
                >
                  {photoLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
              </div>
              
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-file-input"
              />
              
              <p className="text-sm text-slate-500 text-center">
                Click the camera icon to upload a new photo
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Personal Information Section */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="font-heading">Personal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Your full name"
                  data-testid="full-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-slate-50"
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label htmlFor="location">Location (Public)</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Sydney, NSW"
                  data-testid="location-input"
                />
                <p className="text-xs text-slate-500 mt-1">Only suburb/state will be shown publicly</p>
              </div>

              {/* P0 CONTACT DETAILS: Required address fields */}
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                  Contact Address (Required for bookings)
                </p>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="address_line">Street Address</Label>
                    <Input
                      id="address_line"
                      value={formData.address_line}
                      onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
                      placeholder="123 Main Street"
                      data-testid="address-line-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="city">City/Suburb</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Sydney"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <select
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="state-select"
                      >
                        <option value="">Select state</option>
                        {australianStates.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="postcode">Postcode</Label>
                      <Input
                        id="postcode"
                        value={formData.postcode}
                        onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                        placeholder="2000"
                        maxLength={4}
                        data-testid="postcode-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        placeholder="Australia"
                        data-testid="country-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell others a bit about yourself"
                  rows={4}
                  data-testid="bio-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#004B8D] hover:bg-[#003865]"
                disabled={loading}
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Section */}
        <ChangePasswordSection user={user} api={api} />
      </div>
    </div>
  );
};

export default ProfilePage;
